import { formatDate } from "@/services/common";
import React from "react";
import { useTranslation } from "react-i18next";

interface ProfileCardProps {
  user?: {
    id?: number;
    fullname: string;
    email: string;
    phone?: string;
    gender?: 'male' | 'female' | 'other';
    birthday?: string;
    job_title?: string;
    avatar_url?: string;
  };
}

const ProfileCard: React.FC<ProfileCardProps> = ({ user }) => {
  const { t } = useTranslation();

  const infoItems = [
    { label: t("profile.fullName", "Họ và Tên"), value: user?.fullname },
    { label: t("profile.email", "Email"), value: user?.email },
    { label: t("profile.phone", "Số điện thoại"), value: user?.phone || "-" },
    { label: t("profile.gender", "Giới tính"), value: user?.gender || 0 },
    {
      label: t("profile.birthday", "Sinh nhật"),
      value: formatDate(user?.birthday),
    },
    {
      label: t("profile.occupation", "Nghề nghiệp"),
      value: user?.job_title || "-",
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm overflow-hidden">
      <div className="h-24 bg-gradient-to-r from-blue-200 via-primary-100 to-red-100 dark:from-blue-900 dark:via-purple-900 dark:to-orange-900" />

      <div className="flex flex-col items-center -mt-12">
        <div className="relative border-2 border-white dark:border-gray-800 rounded-full shadow-lg mb-3">
          {user?.avatar_url ? (
            <img
              src={user?.avatar_url || "/avatar.jfif"}
              alt={user?.fullname}
              className="w-24 h-24 rounded-full text-center align-middle border-3 bg-white border-white dark:border-gray-800 object-cover shadow-lg"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-24 h-24 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                <svg
                  className="w-12 h-12 text-gray-300 dark:text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
            </div>
          )}
        </div>
        {user && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white !mb-0">
              {user?.fullname}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {user?.email}
            </p>
          </>
        )}
      </div>

      <div className="border-t border-gray-300 dark:!border-gray-700 mx-5 mt-3" />

      <div className="p-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white !mb-3">
          {t("profile.personalInfo", "Thông tin cá nhân")}
        </h3>
        <div className="space-y-2">
          {infoItems.map((item, index) =>
            item.label === t("profile.gender", "Giới tính") ? (
              <div key={index} className="flex text-sm">
                <span className="text-gray-600 dark:text-gray-400 w-24 flex-shrink-0">
                  {item.label}
                </span>
                <span className="text-gray-400 dark:text-gray-500 mr-2">:</span>
                <span className="text-gray-900 dark:text-white break-all">
                  {item.value === 'male'
                    ? t("profile.male", "Nam")
                    : item.value === 'female'
                      ? t("profile.female", "Nữ")
                      : t("profile.other", "Khác")}
                </span>
              </div>
            ) : (
              <div key={index} className="flex text-sm">
                <span className="text-gray-600 dark:text-gray-400 w-24 flex-shrink-0">
                  {item.label}
                </span>
                <span className="text-gray-400 dark:text-gray-500 mr-2">:</span>
                <span className="text-gray-900 dark:text-white break-all">
                  {item.value}
                </span>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
